import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import {
  type CompactRulesRequest,
  chatBuilderService,
  type GenerateRulesRequest,
  type UpdateConfigurationRequest,
} from '@/services/chatBuilder'
import { useAuthStore } from '@/stores/auth'
import { buildDesignChange } from '@/stores/chatBuilder/designHistoryChange'
import type {
  ChatConfiguration,
  ChatConfigurationResponse,
} from '@/stores/chatBuilderStore'
import { useChatBuilderStore } from '@/stores/chatBuilderStore'
import { mergeLauncherPatch } from '@/utils/mergeLauncherPatch'
import { invalidateNotifications } from '@/utils/notifications'
import { configurationKeys } from './useConfigurations'

export interface ConfigurationsQueryData {
  configurations: ChatConfiguration[]
  total: number
  page: number
  perPage: number
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// Merges each patched section one level deep, as the server does, so partial sections keep their siblings.
function mergeSections(previous: object, patch: object) {
  const before: Record<string, unknown> = { ...previous }

  return Object.fromEntries(
    Object.entries(patch).map(([key, value]) => {
      const existing = before[key]

      return [
        key,
        isRecord(existing) && isRecord(value)
          ? { ...existing, ...value }
          : value,
      ]
    })
  )
}

export const UPDATE_CONFIGURATION_MUTATION_KEY = [
  'configurations',
  'update',
] as const

// Mutation handler functions
const createConfigurationHandlers = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId?: string
) => ({
  onSuccess: (newConfig: ChatConfiguration) => {
    // Lists are keyed by their params, so match the prefix to reach the dashboard's list too.
    queryClient.setQueriesData(
      { queryKey: configurationKeys.lists(orgId) },
      (oldData: ConfigurationsQueryData | undefined) => {
        if (!oldData) return oldData

        return {
          ...oldData,
          configurations: [newConfig, ...oldData.configurations],
          total: oldData.total + 1,
        }
      }
    )

    // Cache the new configuration individually — useConfiguration expects
    // the ChatConfigurationResponse envelope (with .data), not the raw
    // ChatConfiguration. Without the wrapper, useConfiguration treats the
    // cache as fresh but returns `configuration = undefined`, which makes
    // consumers (e.g. the onboarding preview) hang in a loading state.
    queryClient.setQueryData(configurationKeys.detail(newConfig.id, orgId), {
      success: true,
      data: newConfig,
    })

    // Refresh notifications
    invalidateNotifications()
  },
})

const updateConfigurationHandlers = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId?: string
) => ({
  onMutate: async ({
    id,
    data,
  }: {
    id: string
    data: UpdateConfigurationRequest
  }) => {
    // Cancel outgoing refetches
    await queryClient.cancelQueries({
      queryKey: configurationKeys.detail(id, orgId),
    })

    // Snapshot the previous value (cache stores ChatConfigurationResponse format)
    const previousConfig = queryClient.getQueryData<ChatConfigurationResponse>(
      configurationKeys.detail(id, orgId)
    )

    // Optimistically update the individual config with deep merge
    // Extract .data for the actual config
    const previousConfigData = previousConfig?.data

    if (previousConfigData) {
      const designPatch = data.designConfig
      const launcherPatch = designPatch?.launcher
      const launcherUpdate = launcherPatch
        ? {
            launcher: mergeLauncherPatch(
              previousConfigData.designConfig.launcher,
              launcherPatch
            ),
          }
        : {}
      const designUpdate = designPatch
        ? {
            ...previousConfigData.designConfig,
            ...mergeSections(previousConfigData.designConfig, designPatch),
            ...launcherUpdate,
          }
        : previousConfigData.designConfig

      const optimisticConfig = {
        ...previousConfigData,
        ...data,
        // Deep merge nested config objects
        designConfig: designUpdate,
        productConfig: data.productConfig
          ? { ...previousConfigData.productConfig, ...data.productConfig }
          : previousConfigData.productConfig,
        rulesConfig: data.rulesConfig
          ? { ...previousConfigData.rulesConfig, ...data.rulesConfig }
          : previousConfigData.rulesConfig,
      }

      queryClient.setQueryData(configurationKeys.detail(id, orgId), {
        ...previousConfig,
        data: optimisticConfig,
      })

      // Also update it in the configurations list
      queryClient.setQueryData(
        configurationKeys.list(orgId),
        (oldData: ConfigurationsQueryData | undefined) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            configurations: oldData.configurations.map((config) =>
              config.id === id ? optimisticConfig : config
            ),
          }
        }
      )
    }

    return { previousConfig }
  },

  onError: (
    _err: unknown,
    { id }: { id: string },
    context: { previousConfig?: ChatConfigurationResponse } | undefined
  ) => {
    // Rollback on error
    if (context?.previousConfig) {
      queryClient.setQueryData(
        configurationKeys.detail(id, orgId),
        context.previousConfig
      )
    }
  },

  onSuccess: (
    updatedConfig: ChatConfiguration,
    { id, data }: { id: string; data: UpdateConfigurationRequest },
    context: { previousConfig?: ChatConfigurationResponse } | undefined
  ) => {
    const before = context?.previousConfig?.data
    const change = before && buildDesignChange(before, updatedConfig, data)

    if (change) useChatBuilderStore.getState().recordDesignRevision(id, change)

    // An earlier response would drop a later save's optimistic patch, so only the last pending save writes.
    const isLastPendingUpdate =
      queryClient.isMutating({
        mutationKey: UPDATE_CONFIGURATION_MUTATION_KEY,
      }) === 1

    if (isLastPendingUpdate) {
      // useConfiguration expects the ChatConfigurationResponse envelope with { success, data }
      queryClient.setQueryData(configurationKeys.detail(id, orgId), {
        success: true,
        data: updatedConfig,
      })

      queryClient.setQueryData(
        configurationKeys.list(orgId),
        (oldData: ConfigurationsQueryData | undefined) => {
          if (!oldData) return oldData

          return {
            ...oldData,
            configurations: oldData.configurations.map((existing) =>
              existing.id === id ? updatedConfig : existing
            ),
          }
        }
      )
    }

    queryClient.invalidateQueries({ queryKey: configurationKeys.lists(orgId) })
  },
})

const deleteConfigurationHandlers = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId?: string
) => ({
  onMutate: async (id: string) => {
    // Cancel outgoing refetches
    await queryClient.cancelQueries({
      queryKey: configurationKeys.lists(orgId),
    })
    await queryClient.cancelQueries({
      queryKey: configurationKeys.detail(id, orgId),
    })

    // Snapshot the previous values
    const previousConfigs = queryClient.getQueryData<ConfigurationsQueryData>(
      configurationKeys.list(orgId)
    )
    const previousConfig = queryClient.getQueryData<ChatConfigurationResponse>(
      configurationKeys.detail(id, orgId)
    )

    // Optimistically remove from cache
    queryClient.setQueryData(
      configurationKeys.list(orgId),
      (oldData: ConfigurationsQueryData | undefined) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          configurations: oldData.configurations.filter(
            (config) => config.id !== id
          ),
          total: Math.max(0, oldData.total - 1),
        }
      }
    )

    // Remove individual config from cache
    queryClient.removeQueries({ queryKey: configurationKeys.detail(id, orgId) })

    return { previousConfigs, previousConfig }
  },

  onError: (
    _err: unknown,
    id: string,
    context:
      | {
          previousConfigs?: ConfigurationsQueryData
          previousConfig?: ChatConfigurationResponse
        }
      | undefined
  ) => {
    // Rollback on error - restore both list and detail caches
    if (context?.previousConfigs) {
      queryClient.setQueryData(
        configurationKeys.list(orgId),
        context.previousConfigs
      )
    }

    if (context?.previousConfig) {
      queryClient.setQueryData(
        configurationKeys.detail(id, orgId),
        context.previousConfig
      )
    }
  },

  onSettled: () => {
    // Refetch configurations list
    queryClient.invalidateQueries({ queryKey: configurationKeys.lists(orgId) })
  },
})

const publishConfigurationHandlers = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId?: string
) => ({
  onSuccess: (
    response: { publishedAt?: string },
    { id }: { id: string; description: string }
  ) => {
    // Update the configuration status to published (cache stores ChatConfigurationResponse)
    queryClient.setQueryData(
      configurationKeys.detail(id, orgId),
      (oldResponse: ChatConfigurationResponse | undefined) => {
        if (!oldResponse?.data) return oldResponse

        return {
          ...oldResponse,
          data: {
            ...oldResponse.data,
            status: 'published' as const,
            publishedAt: response.publishedAt,
          },
        }
      }
    )

    // Update in configurations list
    queryClient.setQueryData(
      configurationKeys.list(orgId),
      (oldData: ConfigurationsQueryData | undefined) => {
        if (!oldData) return oldData
        return {
          ...oldData,
          configurations: oldData.configurations.map((config) =>
            config.id === id
              ? {
                  ...config,
                  status: 'published' as const,
                  publishedAt: response.publishedAt,
                }
              : config
          ),
        }
      }
    )
  },
})

const renameConfigurationHandlers = (
  queryClient: ReturnType<typeof useQueryClient>,
  orgId?: string
) => ({
  onMutate: async ({ id, name }: { id: string; name: string }) => {
    // Cancel outgoing refetches
    await queryClient.cancelQueries({
      queryKey: configurationKeys.detail(id, orgId),
    })

    // Snapshot the previous value (cache stores ChatConfigurationResponse format)
    const previousConfig = queryClient.getQueryData<ChatConfigurationResponse>(
      configurationKeys.detail(id, orgId)
    )

    // Optimistically update
    const previousConfigData = previousConfig?.data

    if (previousConfigData) {
      const optimisticConfig = { ...previousConfigData, name }

      queryClient.setQueryData(configurationKeys.detail(id, orgId), {
        ...previousConfig,
        data: optimisticConfig,
      })

      // Also update in list
      queryClient.setQueryData(
        configurationKeys.list(orgId),
        (oldData: ConfigurationsQueryData | undefined) => {
          if (!oldData) return oldData
          return {
            ...oldData,
            configurations: oldData.configurations.map((config) =>
              config.id === id ? optimisticConfig : config
            ),
          }
        }
      )
    }

    return { previousConfig }
  },

  onError: (
    _err: unknown,
    { id }: { id: string },
    context: { previousConfig?: ChatConfigurationResponse } | undefined
  ) => {
    // Rollback on error
    if (context?.previousConfig) {
      queryClient.setQueryData(
        configurationKeys.detail(id, orgId),
        context.previousConfig
      )
    }
  },

  onSettled: (
    data: ChatConfiguration | undefined,
    _error: unknown,
    { id }: { id: string }
  ) => {
    // Refetch to get latest data - wrap in response format
    if (data) {
      queryClient.setQueryData(configurationKeys.detail(id, orgId), {
        success: true,
        data,
      })
    }

    queryClient.invalidateQueries({ queryKey: configurationKeys.lists(orgId) })
  },
})

/**
 * Hook for all configuration mutations with optimistic updates
 * Provides create, update, delete, publish, and rename operations
 */
export function useConfigurationMutations() {
  const queryClient = useQueryClient()
  const { organization } = useAuthStore()

  // Get handler functions with bound queryClient and orgId
  const createHandlers = createConfigurationHandlers(
    queryClient,
    organization?.id
  )
  const updateHandlers = updateConfigurationHandlers(
    queryClient,
    organization?.id
  )
  const deleteHandlers = deleteConfigurationHandlers(
    queryClient,
    organization?.id
  )
  const publishHandlers = publishConfigurationHandlers(
    queryClient,
    organization?.id
  )
  const renameHandlers = renameConfigurationHandlers(
    queryClient,
    organization?.id
  )

  // Create configuration mutation
  const createConfigurationMutation = useMutation({
    mutationFn: (
      data: Parameters<typeof chatBuilderService.createConfiguration>[0]
    ) => chatBuilderService.createConfiguration(data),
    ...createHandlers,
  })
  const createGuidedConfigurationMutation = useMutation({
    mutationFn: (
      data: Parameters<typeof chatBuilderService.createGuidedConfiguration>[0]
    ) => chatBuilderService.createGuidedConfiguration(data),
    ...createHandlers,
  })
  // Update configuration mutation
  const updateConfigurationMutation = useMutation({
    mutationKey: UPDATE_CONFIGURATION_MUTATION_KEY,
    mutationFn: ({
      id,
      data,
    }: {
      id: string
      data: Parameters<typeof chatBuilderService.updateConfiguration>[1]
    }) => chatBuilderService.updateConfiguration(id, data),
    ...updateHandlers,
  })
  // Delete configuration mutation
  const deleteConfigurationMutation = useMutation({
    mutationFn: (id: string) => chatBuilderService.deleteConfiguration(id),
    ...deleteHandlers,
  })
  // Publish configuration mutation
  const publishConfigurationMutation = useMutation({
    mutationFn: ({ id, description }: { id: string; description: string }) =>
      chatBuilderService.publishConfiguration(id, description),
    ...publishHandlers,
  })

  // Rename configuration mutation
  const renameConfigurationMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      chatBuilderService.renameConfiguration(id, name),
    ...renameHandlers,
  })

  const duplicateConfigurationMutation = useMutation({
    mutationFn: (id: string) => chatBuilderService.duplicateConfiguration(id),
    ...createHandlers,
  })

  // Generate rules mutation
  const generateRulesMutation = useMutation({
    mutationFn: (request: GenerateRulesRequest) =>
      chatBuilderService.generateRules(request),
  })

  const compactRulesMutation = useMutation({
    mutationFn: (request: CompactRulesRequest) =>
      chatBuilderService.compactRules(request),
  })

  // Wrapper functions for easier use
  const createConfiguration = useCallback(
    (data: Parameters<typeof chatBuilderService.createConfiguration>[0]) =>
      createConfigurationMutation.mutateAsync(data),
    [createConfigurationMutation]
  )
  const createGuidedConfiguration = useCallback(
    (
      data: Parameters<typeof chatBuilderService.createGuidedConfiguration>[0]
    ) => createGuidedConfigurationMutation.mutateAsync(data),
    [createGuidedConfigurationMutation]
  )
  const updateConfiguration = useCallback(
    (
      id: string,
      data: Parameters<typeof chatBuilderService.updateConfiguration>[1]
    ) => updateConfigurationMutation.mutateAsync({ id, data }),
    [updateConfigurationMutation]
  )
  const deleteConfiguration = useCallback(
    (id: string) => deleteConfigurationMutation.mutateAsync(id),
    [deleteConfigurationMutation]
  )
  const publishConfiguration = useCallback(
    (id: string, description: string) =>
      publishConfigurationMutation.mutateAsync({ id, description }),
    [publishConfigurationMutation]
  )

  const renameConfiguration = useCallback(
    (id: string, name: string) =>
      renameConfigurationMutation.mutateAsync({ id, name }),
    [renameConfigurationMutation]
  )

  const duplicateConfiguration = useCallback(
    (id: string) => duplicateConfigurationMutation.mutateAsync(id),
    [duplicateConfigurationMutation]
  )

  const generateRules = useCallback(
    (request: GenerateRulesRequest) =>
      generateRulesMutation.mutateAsync(request),
    [generateRulesMutation]
  )

  const compactRules = useCallback(
    (request: CompactRulesRequest) => compactRulesMutation.mutateAsync(request),
    [compactRulesMutation]
  )

  return {
    // Actions
    createConfiguration,
    createGuidedConfiguration,
    updateConfiguration,
    deleteConfiguration,
    publishConfiguration,
    renameConfiguration,
    duplicateConfiguration,
    generateRules,
    compactRules,

    // Loading states
    isCreating:
      createConfigurationMutation.isPending ||
      createGuidedConfigurationMutation.isPending,
    isUpdating: updateConfigurationMutation.isPending,
    isDeleting: deleteConfigurationMutation.isPending,
    isPublishing: publishConfigurationMutation.isPending,
    isRenaming: renameConfigurationMutation.isPending,
    isDuplicating: duplicateConfigurationMutation.isPending,
    isGeneratingRules: generateRulesMutation.isPending,
    isCompactingRules: compactRulesMutation.isPending,

    // Errors
    createError:
      createConfigurationMutation.error ||
      createGuidedConfigurationMutation.error,
    updateError: updateConfigurationMutation.error,
    deleteError: deleteConfigurationMutation.error,
    publishError: publishConfigurationMutation.error,
    renameError: renameConfigurationMutation.error,
    generateRulesError: generateRulesMutation.error,

    // Mutation objects for advanced usage
    createConfigurationMutation,
    updateConfigurationMutation,
    deleteConfigurationMutation,
    publishConfigurationMutation,
    renameConfigurationMutation,
    generateRulesMutation,
  }
}
